// Per-tenant feature flags. Mirrors `src/railyard/feature_flags.py`.
//
// Used to hide workspace surfaces for migrated DAI cohorts (see
// `docs/dai-migration.md`). Source-of-truth lives server-side on
// `TenantState.disabled_features`; the web reads the resolved list from
// `GET /api/workspace/settings`.

import { useEffect, useState } from "react";
import { getWorkspaceSettings } from "@/lib/api/usage";

export const FEATURE_CONNECTORS_PANEL = "connectors_panel";
export const FEATURE_ROUTINES = "routines";
export const FEATURE_AUDIT_PANEL = "audit_panel";
export const FEATURE_OUTCOMES = "outcomes";
export const FEATURE_COWORK_PLUGIN_PROMO = "cowork_plugin_promo";
export const FEATURE_ONBOARDING_GATE = "onboarding_gate";
export const FEATURE_TASK_VOICE = "task_voice";

type FetchResult = {
  /** False when the HTTP call failed (caller should retry / fail-open). */
  ok: boolean;
  list: string[];
  /** The cacheKey this request was started for — ignore if it no longer matches. */
  key: string | null;
};

// Module-level cache so a single page load makes one settings request,
// even if multiple components call `useDisabledFeatures`. The settings
// endpoint also drives the existing web-tools toggle; the workspace
// settings panel itself owns the canonical mutation flow.
//
// `loaded` flips true ONLY after a successful fetch (not on failure), so a
// caller can fail closed on a gated surface: until we have a real answer,
// "is this feature disabled?" is unknown, and a hidden-by-default feature
// must stay hidden rather than flash on during the load window. A failed
// fetch leaves `loaded` false so the next caller retries.
//
// Cache + inflight are keyed by tenant/user (`cacheKey`) so a principal
// switch cannot reuse another workspace's flags, and a stale in-flight
// response from the previous key cannot mark the new key as successfully
// loaded (ENG-3641 / codex P2 on #2045).
let cached: string[] | null = null;
let loaded = false;
let cachedForKey: string | null = null;
let inflight: { key: string | null; promise: Promise<FetchResult> } | null = null;

function fetchDisabledFeatures(requestKey: string | null): Promise<FetchResult> {
  // Hit only when this exact key was the one that populated the cache.
  if (loaded && cached && cachedForKey === requestKey) {
    return Promise.resolve({ ok: true, list: cached, key: requestKey });
  }
  // Reuse an in-flight request for the SAME key only — a different key must
  // start its own fetch rather than await (and mis-read) the previous one.
  if (inflight && inflight.key === requestKey) {
    return inflight.promise;
  }

  const keyAtStart = requestKey;
  const promise: Promise<FetchResult> = getWorkspaceSettings()
    .then((s) => {
      const list = s.disabled_features ?? [];
      // Commit to the module cache only if this request is still the active
      // inflight for its key. A newer principal switch replaces `inflight`,
      // so a late completion from the old key is ignored here (callers also
      // drop results whose `key` no longer matches their effect's cacheKey).
      if (inflight?.promise === promise && inflight.key === keyAtStart) {
        cached = list;
        loaded = true;
        cachedForKey = keyAtStart;
      }
      return { ok: true, list, key: keyAtStart };
    })
    .catch(() => ({ ok: false, list: [] as string[], key: keyAtStart }))
    .finally(() => {
      if (inflight?.promise === promise) {
        inflight = null;
      }
    });

  inflight = { key: keyAtStart, promise };
  return promise;
}

export type DisabledFeaturesState = {
  disabled: Set<string>;
  loaded: boolean;
  /** True after retries exhausted without a successful settings fetch. */
  failed: boolean;
};

/**
 * Disabled-features set plus whether a successful fetch has resolved.
 *
 * Callers that gate a hidden-by-default surface should require `loaded`
 * before treating the feature as enabled (fail closed); callers that gate
 * an always-on-unless-disabled surface can ignore it (optimistic show).
 *
 * Options (ENG-3641):
 * - `enabled` — defer the fetch until the account is bootstrapped (AuthGuard
 *   passes `Boolean(me)` so we don't 401 pre-bootstrap and stick loaded=false).
 * - `cacheKey` — active tenant/user id; when it changes, invalidate the
 *   module cache so a principal switch can't reuse another tenant's flags.
 */
export function useDisabledFeaturesState(opts?: {
  enabled?: boolean;
  cacheKey?: string | null;
}): DisabledFeaturesState {
  const enabled = opts?.enabled ?? true;
  const cacheKey = opts?.cacheKey ?? null;
  const [state, setState] = useState<DisabledFeaturesState>(() => ({
    disabled: new Set(
      loaded && cachedForKey === cacheKey ? (cached ?? []) : [],
    ),
    loaded: loaded && cachedForKey === cacheKey,
    failed: false,
  }));

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let attempts = 0;
    const maxAttempts = 3;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    // Principal/tenant changed — drop the previous tenant's flag set from
    // local state. Module cache is only reused when cachedForKey matches.
    if (cacheKey !== null && cacheKey !== cachedForKey) {
      setState({ disabled: new Set(), loaded: false, failed: false });
    }

    const run = () => {
      attempts += 1;
      void fetchDisabledFeatures(cacheKey).then((result) => {
        if (!alive) return;
        // Stale completion from a previous key (effect already re-ran).
        if (result.key !== cacheKey) return;
        if (result.ok) {
          setState({
            disabled: new Set(result.list),
            loaded: true,
            failed: false,
          });
          return;
        }
        if (attempts < maxAttempts) {
          // Brief backoff then retry — covers a transient 5xx after bootstrap.
          retryTimer = setTimeout(run, 500 * attempts);
          return;
        }
        setState({ disabled: new Set(), loaded: false, failed: true });
      });
    };
    run();

    return () => {
      alive = false;
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [enabled, cacheKey]);

  return state;
}

export function useDisabledFeatures(): Set<string> {
  return useDisabledFeaturesState().disabled;
}

/** Seed the cache from a `GET /api/me` response (ENG-5390).
 *
 * AuthGuard holds the whole shell until these flags resolve, and they used to
 * arrive from a SEPARATE settings request issued only after /api/me returned —
 * two strictly serial round trips before the app could paint. /api/me now
 * carries the list, so the second request becomes a cache hit.
 *
 * Sets `cachedForKey` as well as `loaded`, which is what
 * `invalidateDisabledFeaturesCache` deliberately does not do (it is for
 * post-mutation refresh, where the key is already correct). Without the key the
 * hook's `cachedForKey === cacheKey` test fails and it refetches anyway.
 *
 * Ignores an absent list rather than seeding []: on an API revision that
 * predates the field, [] would mean "nothing is disabled" and flash gated
 * surfaces on. Absent must fall through to the settings fetch.
 */
export function seedDisabledFeatures(
  list: string[] | null | undefined,
  key: string | null,
): void {
  if (!Array.isArray(list)) return;
  cached = list;
  loaded = true;
  cachedForKey = key;
  inflight = null;
}

// Test/admin hook — call after `updateWorkspaceSettings` so the next
// `useDisabledFeatures` reflects the new state without a hard reload. A
// provided list is authoritative (marks loaded); clearing forces a refetch.
export function invalidateDisabledFeaturesCache(next?: string[]): void {
  cached = next ?? null;
  loaded = next !== undefined;
  if (next === undefined) {
    cachedForKey = null;
    inflight = null;
  }
}
